export type BehaviorHealthOutput = {
  behaviorHealth: {
    communication: string[];
    affiliation: string[];
    ageLimit: string[];
    conditionsTreated: string[];
    levelOfCare: string[];
  };
  crisisServices: Array<{
    communication: string[];
    type: string[];
    telecom: {
      phone: string[];
      email: string[];
    };
    coverageArea: string[];
    availableTime: any[];
    ageLimit: string[];
  }>;
};

type AgeRange = { low?: number; high?: number };

const AGE_GROUPS: Array<{ label: string; low: number; high: number }> = [
  { label: '(0-12)', low: 0, high: 12 },
  { label: '(13-17)', low: 13, high: 17 },
  { label: '(18-64)', low: 18, high: 64 },
  { label: '(65+)', low: 65, high: Number.POSITIVE_INFINITY },
];

function uniqStrings(values: any[]): string[] {
  const s = new Set<string>();
  values.forEach(v => {
    const str = (v ?? '').toString().trim();
    if (str) s.add(str);
  });
  return Array.from(s);
}

function extractDisplays(arr: any[] | null | undefined): string[] {
  if (!Array.isArray(arr)) return [];
  return uniqStrings(arr.map(x => x?.display));
}

function extractTexts(arr: any[] | null | undefined): string[] {
  if (!Array.isArray(arr)) return [];
  return uniqStrings(arr.map(x => x?.text));
}

function extractCoverageAreaDisplays(arr: any[] | null | undefined): string[] {
  if (!Array.isArray(arr)) return [];
  return uniqStrings(arr.map(x => x?.display));
}

function splitTelecom(telecomArr: any[] | null | undefined): { phone: string[]; email: string[] } {
  const phone: string[] = [];
  const email: string[] = [];
  if (Array.isArray(telecomArr)) {
    telecomArr.forEach(t => {
      const system = (t?.system ?? '').toString().trim();
      const value = (t?.value ?? '').toString().trim();
      if (!value) return;
      if (system === 'phone') phone.push(value);
      if (system === 'email') email.push(value);
    });
  }
  return { phone: uniqStrings(phone), email: uniqStrings(email) };
}

function rangesOverlap(aLow: number, aHigh: number, bLow: number, bHigh: number): boolean {
  const ah = Number.isFinite(aHigh) ? aHigh : Number.POSITIVE_INFINITY;
  const bh = Number.isFinite(bHigh) ? bHigh : Number.POSITIVE_INFINITY;
  return aLow <= bh && bLow <= ah;
}

function mapAgeLimitRangesToGroups(ageLimitArr: AgeRange[] | null | undefined): string[] {
  if (!Array.isArray(ageLimitArr) || ageLimitArr.length === 0) return [];

  const out = new Set<string>();

  ageLimitArr.forEach(r => {
    const low = Number(r?.low);
    const highRaw = r?.high;
    const high = highRaw === undefined || highRaw === null ? Number.POSITIVE_INFINITY : Number(highRaw);
    if (!Number.isFinite(low)) return;

    AGE_GROUPS.forEach(g => {
      if (rangesOverlap(low, high, g.low, g.high)) out.add(g.label);
    });
  });

  return Array.from(out);
}

function getServicesOrFallbackLocationAsSources(relation: any): { sources: any[]; locationObj: any } {
  const relationServices = relation?.services;

  const rawLocation = relation?.location;
  const locationObj = Array.isArray(rawLocation) ? rawLocation[0] : rawLocation;

  const locationServices = locationObj?.services;

  if (Array.isArray(relationServices) && relationServices.length > 0) {
    return { sources: relationServices, locationObj };
  }

  if (Array.isArray(locationServices) && locationServices.length > 0) {
    return { sources: locationServices, locationObj };
  }

  if (locationObj) {
    return { sources: [locationObj], locationObj };
  }

  return { sources: [], locationObj: undefined };
}

/**
 * Resolves raw Crisis Services source entries from a relation, following this priority:
 * 1. relation.services[] entries where category.text === "Crisis Services"
 * 2. Fallback: relation.location.services (or relation.location[0].services) entries
 *    where category.text === "Crisis Services"
 *
 * Returns an empty array when neither source contains valid Crisis Services entries.
 * Supports both a single location object and an array of locations.
 */
export function getCrisisServicesFromRelation(relation: any): any[] {
  // Normalise location – supports both single object and array
  const rawLocation = relation?.location;
  const locationObj: any = Array.isArray(rawLocation) ? rawLocation[0] : rawLocation;

  // Step 1: Filter relation.services for Crisis Services entries
  const relationServices: any[] = Array.isArray(relation?.services) ? relation.services : [];
  const crisisFromRelation = relationServices.filter(
    (s: any) => (s?.category?.text ?? '').toString().trim() === 'Crisis Services'
  );
  if (crisisFromRelation.length > 0) {
    return crisisFromRelation;
  }

  // Step 2: Fallback to relation.location.services
  const locationServices: any[] = Array.isArray(locationObj?.services) ? locationObj.services : [];
  return locationServices.filter(
    (s: any) => (s?.category?.text ?? '').toString().trim() === 'Crisis Services'
  );
}

/**
 * Extracts Behavior Health (merged) + Crisis Services (array) from ONE relation object.
 * Uses relation.services when present; otherwise falls back to relation.location[0].services.
 */
export function extractBhAndCrisisFromRelation(relation: any): BehaviorHealthOutput {
  const { sources, locationObj } = getServicesOrFallbackLocationAsSources(relation);

  const behaviorHealthSets = {
    communication: new Set<string>(),
    affiliation: new Set<string>(),
    ageLimit: new Set<string>(),
    conditionsTreated: new Set<string>(),
    levelOfCare: new Set<string>(),
  };

  const crisisServices: BehaviorHealthOutput['crisisServices'] = [];

  const levelOfCareFromLocation = (() => {
    const typeArr = locationObj?.type;
    if (!Array.isArray(typeArr)) return [];
    return uniqStrings(typeArr.map((t: any) => t?.text));
  })();

  sources.forEach(src => {
    const categoryText = (src?.category?.text ?? '').toString().trim();

    // Behavior Health => merge into ONE object
    if (categoryText === 'Behavior Health') {
      extractDisplays(src?.communication).forEach(v => behaviorHealthSets.communication.add(v));
      extractTexts(src?.affiliation).forEach(v => behaviorHealthSets.affiliation.add(v));
      extractDisplays(src?.conditionsTreated).forEach(v => behaviorHealthSets.conditionsTreated.add(v));
      mapAgeLimitRangesToGroups(src?.ageLimit).forEach(v => behaviorHealthSets.ageLimit.add(v));
      levelOfCareFromLocation.forEach(v => behaviorHealthSets.levelOfCare.add(v));
    }
  });

  // Crisis Services => keep as ARRAY (no merge), resolved via dedicated DRY helper
  getCrisisServicesFromRelation(relation).forEach(src => {
    crisisServices.push({
      communication: extractDisplays(src?.communication),
      type: extractTexts(src?.type),
      telecom: splitTelecom(src?.telecom),
      coverageArea: extractCoverageAreaDisplays(src?.coverageArea),
      availableTime: Array.isArray(src?.availableTime) ? src.availableTime : [],
      ageLimit: mapAgeLimitRangesToGroups(src?.ageLimit),
    });
  });

  return {
    behaviorHealth: {
      communication: Array.from(behaviorHealthSets.communication),
      affiliation: Array.from(behaviorHealthSets.affiliation),
      ageLimit: Array.from(behaviorHealthSets.ageLimit),
      conditionsTreated: Array.from(behaviorHealthSets.conditionsTreated),
      levelOfCare: Array.from(behaviorHealthSets.levelOfCare),
    },
    crisisServices,
  };
}

/**
 * Convenience helper when you have root.relations (array).
 * Returns one output per relation, preserving order.
 */
export function extractBhAndCrisisFromRelations(relations: any[] | null | undefined): BehaviorHealthOutput[] {
  if (!Array.isArray(relations) || relations.length === 0) return [];
  return relations.map(r => extractBhAndCrisisFromRelation(r));
}
